/**
 * Why a dev panel control may not be usable right now.
 *
 * `blocked` and `inert` are deliberately different. A blocked control cannot
 * work at all (Copy token offline: Keycloak is never initialised, so there is
 * no token). An inert control applies correctly but nothing renders the
 * result (the smartAccount override pushes to a BehaviorSubject and always
 * succeeds, but smart-account data is backed by bank REST and GraphQL, which
 * the offline interceptor does not cover).
 *
 * This module intentionally has no Angular imports so it can be tested
 * without a TestBed.
 */

export type DevAvailabilityTier = "available" | "inert" | "blocked";

/** Environment facts the rules are evaluated against. */
export interface DevAvailabilityContext {
  readonly offlineMode: boolean;
  readonly advisorMode: boolean;
}

export type DevAvailability =
  | { readonly tier: "available" }
  | { readonly tier: Exclude<DevAvailabilityTier, "available">; readonly reason: string };

const AVAILABLE: DevAvailability = { tier: "available" };

interface DevAvailabilityRule {
  readonly tier: Exclude<DevAvailabilityTier, "available">;
  readonly reason: string;
  readonly unavailableWhen: (context: DevAvailabilityContext) => boolean;
}

/**
 * Keep this set small. Every rule is hand-maintained, and a wrong rule
 * misleads in a new way — it buries a control that actually works.
 *
 * Deliberately absent: an auth-level rule. `isAuthLevel3or4$` is hardcoded to
 * `of(true)` in offline mode, so such a rule would report a false positive
 * exactly where it matters.
 */
export const DEV_AVAILABILITY_RULES = {
  requiresOnline: {
    tier: "blocked",
    reason: "Needs Offline mode off — Keycloak isn't initialised",
    unavailableWhen: ({ offlineMode }) => offlineMode,
  },
  requiresOffline: {
    tier: "blocked",
    reason: "Needs Offline mode on",
    unavailableWhen: ({ offlineMode }) => !offlineMode,
  },
  requiresNonAdvisor: {
    tier: "blocked",
    reason: "Not available in advisor context",
    unavailableWhen: ({ advisorMode }) => advisorMode,
  },
  mockedOffline: {
    tier: "inert",
    reason: "Applies, but this data isn't mocked offline",
    unavailableWhen: ({ offlineMode }) => offlineMode,
  },
} as const satisfies Record<string, DevAvailabilityRule>;

export type DevAvailabilityRuleKey = keyof typeof DEV_AVAILABILITY_RULES;

export function resolveAvailability(
  ruleKey: DevAvailabilityRuleKey | undefined,
  context: DevAvailabilityContext,
): DevAvailability {
  if (ruleKey === undefined) {
    return AVAILABLE;
  }

  const rule = DEV_AVAILABILITY_RULES[ruleKey];
  if (!rule.unavailableWhen(context)) {
    return AVAILABLE;
  }

  return { tier: rule.tier, reason: rule.reason };
}
